import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '~/lib/utils';

/**
 * App-local Badge that re-uses the library's visual tokens but adds
 * project-management-specific status variants (`ongoing`, `delayed`)
 * that don't (yet) exist in `@systemzero-inc/ui/primitives/badge`.
 *
 * Mirrors the library Badge styling for shared variants so the surface
 * is visually identical; only the extra variants live here.
 */
const badgeVariants = cva(
  [
    'inline-flex items-center rounded-full border px-2.5 py-0.5',
    'text-xs font-semibold leading-normal transition-colors',
    'focus:outline-hidden focus:ring-1 focus:ring-ring/50',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'border-transparent bg-muted text-muted-foreground',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        outline: 'border-border bg-background text-foreground',
        success: 'border-transparent bg-positive-background-middle text-positive-foreground',
        warning: 'border-transparent bg-alert-background-soft text-alert-foreground',
        destructive: 'border-transparent bg-negative-background-middle text-negative-foreground',
        completed: 'border-transparent bg-completed-background-soft text-completed-foreground',
        ai: 'bg-alpha-20 text-ai border-ai hover:text-ai hover:bg-ai-hover-ghost',
        ongoing: 'border-transparent bg-positive-background-middle text-positive-foreground',
        delayed: 'border-transparent bg-negative-background-middle text-negative-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface BadgeProps extends React.ComponentProps<'span'>, VariantProps<typeof badgeVariants> {
  asChild?: boolean;
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { className, variant, asChild = false, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'span';
  return <Comp ref={ref} data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />;
});

export { badgeVariants };
